import { useState } from 'react';
import { Box, Typography, Button } from '@mui/joy';
import { CaretDown, CaretUp, Compass } from '@phosphor-icons/react';

/**
 * «Как проходить план» — общая инструкция перед первым днём любого модуля.
 * Рисуется как нижний ярус карточки модуля (описание → плашки → инструкция),
 * поэтому у него нет своей рамки: только разделительная линия сверху.
 * Текст согласован владельцем 05.09.2026. Раскрыта при каждом входе в план
 * (владелец, 05.09: не запоминать); «Понятно» сворачивает в строку только
 * на текущий просмотр, по клику на строку раскрывается снова.
 */

const POINTS = [
    {
        title: 'Гейты обязательны.',
        text: 'Тест-гейт закрывает блок тем и открывает следующий день. Порог сдачи 40%, пересдавать можно сколько угодно.',
    },
    {
        title: 'Остальное по желанию.',
        text: 'Мини-тесты, клинические задачи и Anki дополняют друг друга, но решать всё подряд не нужно. Выберите связку под себя: Anki и задачи без мини-тестов, или мини-тесты и задачи без Anki. Главное, чтобы каждый день была практика, а не только чтение.',
    },
    {
        title: 'Anki: любая колода по теме дня.',
        text: 'Подойдёт та, по которой вы уже занимаетесь. Если учите по First Aid, напротив каждой Anki-задачи указана колода FANKI по этому концепту.',
    },
    {
        title: 'Темп ваш.',
        text: 'План рассчитан на день, но два дня на одну тему это нормально. Открытый день никуда не денется.',
    },
];

const divider = (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
const accent = (theme) => theme.palette.mode === 'dark' ? '#DA7756' : '#B84C31';

export default function PlanIntro() {
    const [open, setOpen] = useState(true);

    if (!open) {
        return (
            <Box component="button" type="button" onClick={() => setOpen(true)} sx={{
                width: '100%', mt: 2, pt: 2, pb: 0, px: 0, textAlign: 'left',
                border: 'none', borderTop: '1px solid', borderColor: divider, bgcolor: 'transparent', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 1.25, fontFamily: 'inherit', color: 'inherit',
                '&:hover .plan-intro-title': { color: accent },
            }}>
                <Compass size={18} weight="duotone" />
                <Typography className="plan-intro-title" sx={{ fontSize: '0.88rem', fontWeight: 600, flex: 1, transition: 'color 0.15s' }}>Как проходить план</Typography>
                <CaretDown size={16} />
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid', borderColor: divider }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <Box sx={{
                    width: 32, height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(218,119,86,0.15)' : 'rgba(184,76,49,0.1)', color: accent,
                }}>
                    <Compass size={18} weight="duotone" />
                </Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 650, letterSpacing: '-0.01em', flex: 1 }}>Как проходить план</Typography>
                <Box component="button" type="button" aria-label="Свернуть" onClick={() => setOpen(false)} sx={{
                    border: 'none', bgcolor: 'transparent', cursor: 'pointer', color: 'text.tertiary', p: 0.5, display: 'flex',
                }}>
                    <CaretUp size={16} />
                </Box>
            </Box>

            <Box component="ol" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'grid', gap: 1.25 }}>
                {POINTS.map((p, i) => (
                    <Box component="li" key={p.title} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Box sx={{
                            width: 24, height: 24, borderRadius: '8px', flexShrink: 0, mt: '1px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.78rem', fontWeight: 700, color: accent,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.045)',
                        }}>{i + 1}</Box>
                        <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.65, letterSpacing: '-0.005em', color: 'text.primary' }}>
                            <Box component="span" sx={{ fontWeight: 650 }}>{p.title}</Box>{' '}{p.text}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="sm" variant="solid" color="primary" onClick={() => setOpen(false)} sx={{ borderRadius: '8px', px: 2.5 }}>
                    Понятно
                </Button>
            </Box>
        </Box>
    );
}
